import { Button } from "@jutro/ui";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { Loader } from "components/new/Loader";
import { useDoctorGetDocumentsQuery } from "lib/graphql/megaSchema";
import { toaster } from "lib/tools/toaster";
import { uploadMedicalRecordFile } from "lib/tools/uploadFile";
import { Maybe } from "lib/types";

type DropzoneProps = {
  patientId: Maybe<string>;
};

export const MedicalDocumentationDropzone = ({ patientId }: DropzoneProps) => {
  const { startPolling, stopPolling, refetch } = useDoctorGetDocumentsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      id: patientId,
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (
        data.doctorPatient?.documents
          .filter((d) => d.tidy_status !== null)
          .every((d) => d.tidy_status !== "INPROGRESS")
      ) {
        stopPolling();
      }
    },
  });

  const { getRootProps, getInputProps, open, isDragActive, acceptedFiles } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      accept: {
        "image/*": [".jpeg", ".jpg", ".png"],
        "application/pdf": [".pdf"],
      },
      onDropRejected: () =>
        toaster.error(
          "Wystąpił błąd. Akceptowane typy plików: .jpeg, .jpg, .pdf, .png",
        ),
    });

  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const submitFiles = async () => {
      if (acceptedFiles.length === 0) {
        return;
      }
      try {
        await addFiles(acceptedFiles);
        toaster.success("Dodano plik!");
      } catch {
        toaster.error("Nie udało się dodać pliku!");
        setIsUploading(false);
      }
    };
    submitFiles();
  }, [acceptedFiles]);

  const addFiles = async (acceptedFiles: readonly FileWithPath[]) => {
    if (!patientId) return;

    const uploadFiles = () =>
      Promise.all(
        acceptedFiles.map(async (file) => ({
          name: file.name,
          createdAt: dayjs().format("YYYY-MM-DD HH:mm"),
          source: "DRIVE" as const,
          fileId: await uploadMedicalRecordFile(file, patientId),
        })),
      );

    setIsUploading(true);
    const status = await uploadFiles();
    if (status.some((s) => s.fileId === "ERROR")) {
      throw new Error("some files did not upload properly");
    }

    refetch();
    startPolling(5000);
    setIsUploading(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="font-label">Dodaj pliki</div>

      <div
        className={`${
          isDragActive
            ? "bg-jutro-new-warm-gray-200"
            : "bg-jutro-new-warm-gray-50"
        } font-paragraph-2 max-h-14 rounded-lg p-2 text-center`}
      >
        <div {...getRootProps({ className: "dropzone" })}>
          <input {...getInputProps()} />

          {isUploading ? (
            <Loader size="xs" />
          ) : (
            <div className="flex justify-center">
              <div className="flex">
                <div className="font-label z-1 -mr-1 text-jutro-new-warm-gray-600">
                  Przeciągnij&nbsp;i&nbsp;upuść&nbsp;lub
                </div>

                <Button
                  variant="ghost"
                  type="button"
                  onClick={open}
                  size="condensed"
                  text="wybierz z dysku"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
